import * as Sentry from '@sentry/react';
import { createHashHistory } from 'history';

// The router's history, which React Admin routes with and Sentry's tracing follows.
export const history = createHashHistory();

const apiUrl = `${process.env.REACT_APP_PROTOCOL || 'https'}://${process.env.REACT_APP_API_URL}`;

// Pull request previews are served from codebetaverse.com. A local dev server
// never reports, even when it talks to the production API.
const environment = (() => {
  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  return hostname.endsWith('.codebetaverse.com') ? 'preview' : 'production';
})();

// Names a trace by its #/ route with the ids taken out, so each page is one
// entry in Sentry: /users/5f0c…/show is /users/:id/show. Ids are always
// UUIDs; a number is a tab's index, as in /users/:id/show/1, and stays.
const ID = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi;
export const routeName = pathname => pathname.replace(ID, '/:id');

// Every API request carries the staff member's access token in its query
// string, and a list's filters can carry children's names, so no query string
// is sent: not an API request's, in breadcrumbs and spans, nor the page's,
// after its #/ route. Filter values go into API URLs unencoded, spaces and
// all, so a string that is itself a URL loses everything from its first ?.
// A URL inside other text loses its query string up to the next space or
// quote. Any access token left anywhere else is filtered too.
const URL_START = /^(?:https?:\/\/|\/|#\/)/;
const QUERY = /\?[\s\S]*/;
const QUERY_STRING = /((?:https?:\/\/|#\/)[^\s?#"]*)\?[^\s#"]*/g;
const ACCESS_TOKEN = /(access_token=)[^&#\s"]*/g;

const isPlainObject = value => (
  value !== null && typeof value === 'object' && [Object.prototype, null].includes(Object.getPrototypeOf(value))
);

export const scrub = (value) => {
  if (typeof value === 'string') {
    const unqueried = URL_START.test(value) ? value.replace(QUERY, '') : value.replace(QUERY_STRING, '$1');
    return unqueried.replace(ACCESS_TOKEN, '$1[Filtered]');
  }
  if (Array.isArray(value)) {
    return value.map(scrub);
  }
  if (isPlainObject(value)) {
    return Object.keys(value).reduce((scrubbed, key) => {
      // a request span's query string, on its own
      if (key !== 'http.query') {
        scrubbed[key] = scrub(value[key]);
      }
      return scrubbed;
    }, {});
  }
  return value;
};

// Errors and traces carry the staff member's id and nothing else about them,
// as platform's do. The SDK's own metadata, which it reads after this and
// never sends, is left as it is.
const beforeSend = ({ sdkProcessingMetadata, ...event }) => {
  const userId = localStorage.getItem('user_id');
  return {
    ...scrub({ ...event, user: userId ? { id: userId } : undefined }),
    sdkProcessingMetadata,
  };
};

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  enabled: environment !== 'development',
  environment,
  release: `houston@${process.env.REACT_APP_VERSION}`,
  integrations: [
    Sentry.reactRouterV4BrowserTracingIntegration({
      history,
      beforeStartSpan: context => ({ ...context, name: routeName(context.name) }),
    }),
  ],
  // Trace every page, as platform traces every request, and pass the trace on
  // to the API, so platform's errors link back to the page that caused them.
  tracesSampleRate: 1.0,
  tracePropagationTargets: [apiUrl],
  beforeSend,
  beforeSendTransaction: beforeSend,
});
